import { useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMe, useUsage, useUsageLimits } from '@/api/hooks';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { BreakdownSkeleton, UsageBreakdown } from '@/features/usage/UsageBreakdown';
import { UsageBannerView } from '@/features/usage/UsageBanner';
import { ExportButton } from '@/features/usage/ExportButton';
import { MeterCards, MeterCardsSkeleton } from '@/features/usage/MeterCards';
import { MonthPicker } from '@/features/usage/MonthPicker';
import { PlanCard } from '@/features/usage/PlanCard';
import { bannerOf, currentMonthOf, hasPlan, monthLabel, monthOptions, monthParam } from '@/features/usage/usageModel';

export interface UsageSearch {
  /** `YYYY-MM` (UTC); the current month when absent. */
  month?: string;
}

function Header({ month, picker }: { month: string; picker: React.ReactNode }) {
  return (
    <header className="enter flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Usage</h1>
        <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">
          What your organization&apos;s builds used: build minutes, builds, artifact storage and concurrency. The meters are live; the breakdown below is for {monthLabel(month)}.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">{picker}</div>
    </header>
  );
}

/** Plan, meters and the month's breakdown. Three states each: skeleton with the real geometry, data, designed empty state. */
function UsageBody({ month, selected }: { month: string; selected: string | undefined }) {
  const limits = useUsageLimits();
  const report = useUsage(selected);
  const banner = limits.data ? bannerOf(limits.data) : null;
  return (
    <>
      {banner ? <UsageBannerView banner={banner} /> : null}
      {limits.isError && !limits.data ? <ErrorState title="Couldn't load the plan" error={limits.error} onRetry={() => void limits.refetch()} className="rounded-lg border" /> : null}
      {limits.data && hasPlan(limits.data) ? <PlanCard plan={limits.data.plan} /> : null}
      {limits.data ? <MeterCards limits={limits.data} /> : limits.isError ? null : <MeterCardsSkeleton />}
      {report.isError && !report.data ? <ErrorState title="Couldn't load the usage" error={report.error} onRetry={() => void report.refetch()} className="rounded-lg border" /> : null}
      {report.data ? <UsageBreakdown key={report.data.month} report={report.data} /> : report.isError ? null : <BreakdownSkeleton />}
      <span hidden data-testid="usage-month-shown">{month}</span>
    </>
  );
}

/**
 * Usage (SPEC §18.1, decision 30): what the org's builds used (the meters and the plan's limits once a plan is set for
 * the org; consumption only before), and what the month's builds used by project, app, provider and
 * platform, with the CSV export for invoicing. Org-level and internal: a client (guest) is told so instead of a refusal.
 */
export function UsagePage() {
  const search = useSearch({ strict: false }) as UsageSearch;
  const navigate = useNavigate();
  const me = useMe();
  const selected = monthParam(search.month);
  const current = useMemo(() => currentMonthOf(new Date()), []);
  const month = selected ?? current;
  const report = useUsage(selected);
  const options = monthOptions(report.data?.months ?? [], current, selected);
  const external = me.data ? me.data.orgRole === 'guest' || me.data.user?.isExternal === true : false;

  if (external) {
    return (
      <div data-testid="usage-page" className="flex min-w-0 flex-col gap-5">
        <Header month={month} picker={null} />
        <EmptyState icon="lock" title="Usage is an organization view" description="Plans and usage are visible to your organization's staff. Ask them if you need these figures." />
      </div>
    );
  }
  const picker = (
    <>
      <MonthPicker value={month} options={options} onChange={(m) => void navigate({ to: '/usage', search: { month: m === current ? undefined : m }, replace: true })} />
      <ExportButton month={month} />
    </>
  );
  return (
    <div data-testid="usage-page" className="flex min-w-0 flex-col gap-5">
      <Header month={month} picker={picker} />
      <UsageBody month={month} selected={selected} />
    </div>
  );
}
